// @ts-nocheck
import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import type {} from '@react-three/fiber';
import Skybox from './Skybox';
import POI from './POI';
import WireBackground from './WireBackground';
import CircularBanner from './CircularBanner';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      mesh: any;
      boxGeometry: any;
      meshStandardMaterial: any;
      ambientLight: any;
      directionalLight: any;
    }
  }
}

type SceneProps = {
  isPlaying: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
};

function CameraOrbit({ isPlaying }: SceneProps) {
  const angleRef = useRef(0);
  const radius = 8;
  const speed = 0.15; // radians per second
  // Smooth speed factor to avoid harsh stop/start
  const speedFactorRef = useRef(1);
  // Elevation oscillation: ~5 degrees, 1.5–2 cycles per revolution
  const cyclesPerRevRef = useRef(1.5 + Math.random() * 0.5);
  const phaseRef = useRef(Math.random() * Math.PI * 2);
  const baseElevationRad = Math.asin(0.8 / radius); // preserve previous baseline height
  const elevationAmpRad = (2.5 * Math.PI) / 180; // ~2.5 degrees in radians

  useFrame(({ camera }, delta) => {
    // Smoothly damp orbit speed toward target (1 when playing, 0 when paused)
    const targetSpeedFactor = isPlaying ? 1 : 0;
    const lambda = 1 - Math.exp(-delta / 1.0); // ~1s time constant
    speedFactorRef.current += (targetSpeedFactor - speedFactorRef.current) * lambda;
    angleRef.current += speed * speedFactorRef.current * delta;
    const angle = angleRef.current;
    // Compute elevation with subtle oscillation tied to orbit angle
    const elevation =
      baseElevationRad +
      elevationAmpRad * Math.sin(angle * cyclesPerRevRef.current + phaseRef.current);
    const horizontal = radius * Math.cos(elevation);
    camera.position.x = Math.cos(angle) * horizontal;
    camera.position.z = Math.sin(angle) * horizontal;
    camera.position.y = radius * Math.sin(elevation);
    camera.lookAt(0, 0, 0);
  });

  return null;
}

function AmbientLight({ isPlaying }: { isPlaying: boolean }) {
  const lightRef = useRef<any>(null);
  const intensityRef = useRef(isPlaying ? 0.6 : 0.35);
  useFrame((_, delta) => {
    const target = isPlaying ? 0.7 : 0.45;
    const lambda = 1 - Math.exp(-delta / 1.0); // ~1s time constant
    intensityRef.current += (target - intensityRef.current) * lambda;
    if (lightRef.current) {
      lightRef.current.intensity = intensityRef.current;
    }
  });
  return <ambientLight ref={lightRef} intensity={intensityRef.current} />;
}

function DirectionalKeyLight({ isPlaying }: { isPlaying: boolean }) {
  const lightRef = useRef<any>(null);
  // Match approximate combined brightness of previous two lights:
  // playing ~ 0.8 + 1.3 = 2.1, paused ~ 0.8 + 0.3 = 1.1
  const intensityRef = useRef(isPlaying ? 2.1 : 1.1);
  useFrame((_, delta) => {
    const target = isPlaying ? 2.1 : 1.1;
    const lambda = 1 - Math.exp(-delta / 1.0); // ~1s time constant
    intensityRef.current += (target - intensityRef.current) * lambda;
    if (lightRef.current) {
      lightRef.current.intensity = intensityRef.current;
    }
  });
  return (
    <directionalLight
      ref={lightRef}
      position={[3, 5, 2]}
      intensity={intensityRef.current}
      color={"#ffffff"}
    />
  );
}

function AlbumArt() {
  return <POI />;
}

export default function Scene({ isPlaying, audioRef }: SceneProps) {
  return (
    <Canvas camera={{ position: [0, 0.8, 6], fov: 50 }} dpr={[1, 1.5]} shadows={false}>
      <AmbientLight isPlaying={isPlaying} />
      <DirectionalKeyLight isPlaying={isPlaying} />
      <WireBackground isPlaying={isPlaying} audioRef={audioRef} />
      <Skybox isPlaying={isPlaying} />
      <CameraOrbit isPlaying={isPlaying} />
      <CircularBanner position={[0, 2.45, 0]} scale={0.82} isPlaying={isPlaying} />
      <AlbumArt />
    </Canvas>
  );
}


